import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Clock } from 'lucide-react';
import { TOKENS, limeBtn, displayFont } from '../lib/designTokens';
import { articlesApi } from '../lib/api';
import { LeadMagnetCapture } from '../components/home/LeadMagnetCapture';
import { LocatorFeeder } from '../components/LocatorFeeder';

// Shared content-pillar hub component. Renders an audit-compliant pillar page:
//   eyebrow + h1 + subhead + lime CTA
//   trust signals row
//   featured-articles grid (auto-fetched from /api/articles?category=<cat>)
//   tools strip
//   lead-magnet
//
// Used by /recovery and /longevity. Single source of truth for the layout.
export const PillarHub = ({
  pillar,                    // e.g. "Recovery"
  category,                  // backend category name e.g. "Recovery" or "Longevity"
  eyebrow,
  headline,                  // single short promise
  subhead,
  trustSignals = [],         // [{ label, detail }]
  tools = [],                // [{ label, href, icon }]
  primaryCta,                // { label, href }
  fallbackTagMatches = [],   // article tags to query as fallback if category returns nothing
  testid,
  bgVideo,
  locatorFeeder = null,      // optional <LocatorFeeder /> props object
  partnerFeature = null,     // optional partner feature node (e.g. Bimini on /recovery)
  extraGuides = [],          // pinned internal links shown first in the guides grid
}) => {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = { category, limit: 9, published: true };
        const res = await articlesApi.getAll(params);
        let list = res?.data || [];
        // If category yields nothing, query by tag fallback.
        if (list.length === 0 && fallbackTagMatches.length > 0) {
          const tagRes = await articlesApi.getAll({ limit: 9, published: true });
          list = (tagRes?.data || []).filter(a => {
            const tags = (a.tags || []).map(t => (t || '').toLowerCase());
            return fallbackTagMatches.some(t => tags.includes(t.toLowerCase()));
          });
        }
        if (!cancelled) setArticles(list.slice(0, 9));
      } catch (err) {
        if (!cancelled) {
          console.error(`PillarHub (${category}) fetch failed:`, err);
          setArticles([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [category, fallbackTagMatches]);

  return (
    <main
      className="min-h-screen pt-20"
      style={{ backgroundColor: TOKENS.canvas }}
      data-testid={testid || 'pillar-hub'}
    >
      {/* ── Hero ───────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {bgVideo && (
          <div className="absolute inset-0 z-0">
            <video autoPlay muted loop playsInline className="w-full h-full object-cover" style={{ opacity: 0.14 }}>
              <source src={bgVideo} type="video/mp4" />
            </video>
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(135deg, ${TOKENS.canvas} 0%, rgba(11,11,12,0.85) 70%, rgba(11,11,12,0.55) 100%)` }}
            />
          </div>
        )}
        <div className="container-ninja relative z-10 py-20 md:py-28">
          <div className="max-w-3xl">
            <p
              className="text-[11px] uppercase tracking-[0.25em] mb-3"
              style={{ color: TOKENS.lime, ...displayFont }}
              data-testid={`${testid}-eyebrow`}
            >
              {eyebrow}
            </p>
            <h1
              className="text-white text-5xl md:text-6xl lg:text-7xl mb-6 leading-[1.04]"
              style={displayFont}
              data-testid={`${testid}-h1`}
            >
              {headline}
            </h1>
            <p className="text-zinc-300 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
              {subhead}
            </p>
            {primaryCta && (
              <Link
                to={primaryCta.href}
                className="inline-flex items-center gap-2 px-7 py-4 text-sm md:text-base uppercase tracking-wider transition-all duration-300 hover:-translate-y-0.5"
                style={limeBtn}
                data-testid={`${testid}-primary-cta`}
              >
                {primaryCta.label}
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* ── Trust signals row ──────────────────────────────── */}
      {trustSignals.length > 0 && (
        <section
          className="border-t border-b"
          style={{ borderColor: 'rgba(255,255,255,0.06)', backgroundColor: TOKENS.surface1 }}
        >
          <div className="container-ninja">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-zinc-800/60">
              {trustSignals.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className="flex items-center gap-3 px-3 md:px-6 py-5"
                    data-testid={`${testid}-trust-${idx}`}
                  >
                    {Icon && <Icon className="w-5 h-5 flex-shrink-0" style={{ color: TOKENS.lime }} strokeWidth={1.5} />}
                    <div>
                      <p
                        className="text-white text-xs md:text-sm uppercase tracking-wider leading-none mb-1"
                        style={displayFont}
                      >
                        {item.label}
                      </p>
                      <p className="text-zinc-400 text-[11px] md:text-xs leading-tight">{item.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Featured articles grid ─────────────────────────── */}
      <section className="py-20 md:py-24" data-testid={`${testid}-articles-section`}>
        <div className="container-ninja">
          <div className="mb-10 md:mb-14 max-w-2xl">
            <p
              className="text-[11px] uppercase tracking-[0.25em] mb-3"
              style={{ color: TOKENS.lime, ...displayFont }}
            >
              Featured guides
            </p>
            <h2 className="text-white text-3xl md:text-4xl lg:text-5xl leading-tight" style={displayFont}>
              The {pillar} library
            </h2>
          </div>
          {articles.length === 0 && extraGuides.length === 0 ? (
            <p className="text-zinc-500 text-sm">No articles yet — check back soon.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {extraGuides.map((g) => (
                <Link
                  key={g.href}
                  to={g.href}
                  className="group block transition-all duration-300 hover:-translate-y-1"
                  style={{ backgroundColor: TOKENS.surface1, border: '1px solid rgba(255,255,255,0.06)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = TOKENS.limeBorder; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
                  data-testid={`${testid}-guide-${g.href.replace(/\//g, '')}`}
                >
                  <div className="p-5">
                    <span
                      className="text-[10px] uppercase tracking-[0.2em] inline-block mb-3"
                      style={{ color: TOKENS.lime, ...displayFont }}
                    >
                      {g.category || pillar}
                    </span>
                    <h3
                      className="text-white text-lg md:text-xl mb-2 leading-tight group-hover:text-[#C7FA3E] transition-colors"
                      style={displayFont}
                    >
                      {g.title}
                    </h3>
                    {g.blurb && <p className="text-zinc-500 text-sm leading-relaxed">{g.blurb}</p>}
                  </div>
                </Link>
              ))}
              {articles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/blogs/article/${a.slug}`}
                  className="group block transition-all duration-300 hover:-translate-y-1"
                  style={{ backgroundColor: TOKENS.surface1, border: '1px solid rgba(255,255,255,0.06)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = TOKENS.limeBorder; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
                  data-testid={`${testid}-article-${a.slug}`}
                >
                  {a.featured_image && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={a.featured_image}
                        alt={a.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-5">
                    <span
                      className="text-[10px] uppercase tracking-[0.2em] inline-block mb-3"
                      style={{ color: TOKENS.lime, ...displayFont }}
                    >
                      {a.category || pillar}
                    </span>
                    <h3
                      className="text-white text-lg md:text-xl mb-2 leading-tight group-hover:text-[#C7FA3E] transition-colors line-clamp-3"
                      style={displayFont}
                    >
                      {a.title}
                    </h3>
                    {a.read_time && (
                      <span className="inline-flex items-center gap-1 text-zinc-500 text-xs mt-2">
                        <Clock className="w-3 h-3" /> {a.read_time} min read
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {partnerFeature && (
        <section className="container-ninja" data-testid={`${testid}-partner-feature`}>
          {partnerFeature}
        </section>
      )}

      {/* ── Tools strip ────────────────────────────────────── */}
      {tools.length > 0 && (
        <section
          className="py-16 md:py-20"
          style={{ backgroundColor: TOKENS.surface1, borderTop: '1px solid rgba(255,255,255,0.05)' }}
          data-testid={`${testid}-tools-section`}
        >
          <div className="container-ninja">
            <div className="mb-8">
              <p
                className="text-[11px] uppercase tracking-[0.25em] mb-2"
                style={{ color: TOKENS.lime, ...displayFont }}
              >
                Run the numbers
              </p>
              <h2 className="text-white text-2xl md:text-3xl" style={displayFont}>
                {pillar} tools &amp; calculators
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {tools.map((tool) => {
                const Icon = tool.icon;
                return (
                  <Link
                    key={tool.label}
                    to={tool.href}
                    className="group flex flex-col items-start gap-3 p-5 transition-all duration-300 hover:-translate-y-0.5"
                    style={{ backgroundColor: TOKENS.surface2, border: '1px solid rgba(255,255,255,0.06)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = TOKENS.limeBorder; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
                    data-testid={`${testid}-tool-${tool.label.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {Icon && <Icon className="w-5 h-5" style={{ color: TOKENS.lime }} strokeWidth={1.5} />}
                    <span
                      className="text-white text-sm leading-tight group-hover:text-[#C7FA3E] transition-colors inline-flex items-center gap-2"
                      style={displayFont}
                    >
                      {tool.label}
                      <ArrowUpRight className="w-3.5 h-3.5 opacity-50" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {locatorFeeder && (
        <section className="container-ninja">
          <LocatorFeeder {...locatorFeeder} testId={`${testid}-locator-feeder`} />
        </section>
      )}

      {/* ── Lead magnet capture ────────────────────────────── */}
      <LeadMagnetCapture />
    </main>
  );
};
